/**
 * Boîtes de dialogue.
 *
 * L'application Android confirme systématiquement les actions destructives
 * (suppression de trace, de marqueur, de détour) : on conserve cette règle.
 */

export interface DialogAction {
  readonly label: string
  readonly onClick?: () => void
  readonly primary?: boolean
  /**
   * Action destructive, placée à l'écart des autres.
   *
   * Reprend le bouton « neutre » des dialogues Material : la suppression est
   * à gauche, loin du bouton de validation, pour ne pas être touchée par
   * réflexe.
   */
  readonly destructive?: boolean
  /** Empêche la fermeture automatique après le clic. */
  readonly keepOpen?: boolean
  /**
   * Bouton bloqué tant que le corps n'a pas été lu jusqu'au bout.
   *
   * Réservé à ce qu'on accepte : un texte qu'on ne peut approuver qu'après
   * l'avoir fait défiler entièrement. Sans barre de défilement — texte court,
   * grand écran —, il n'y a rien à faire défiler et le bouton est actif
   * d'emblée : exiger un geste impossible enfermerait l'utilisateur.
   */
  readonly readingRequired?: boolean
}

interface DialogOptions {
  readonly title: string
  readonly body?: string | HTMLElement
  readonly actions: readonly DialogAction[]
  /** Autoriser la fermeture par la touche d'échappement ou le fond. */
  readonly dismissible?: boolean
  /**
   * Appelé à toute fermeture, quelle qu'en soit la cause.
   *
   * Permet de traiter l'abandon — touche d'échappement, clic sur le fond —
   * comme une réponse à part entière, et non comme un silence.
   */
  readonly onClose?: () => void
  /**
   * Ligne affichée tant qu'un bouton `readingRequired` reste bloqué.
   *
   * Un bouton grisé sans explication passe pour une panne : le seul moyen de
   * distinguer « lisez la suite » de « l'application ne répond plus » est de
   * le dire.
   */
  readonly readingHint?: string
}

/**
 * Marge, en pixels, sous laquelle le corps est tenu pour lu jusqu'au bout.
 *
 * Le zoom du navigateur et les écrans à densité fractionnaire rendent des
 * hauteurs qui ne tombent pas juste : `scrollHeight` dépasse alors d'un ou
 * deux pixels la somme réellement atteignable, et un texte lu en entier ne
 * débloquerait jamais son bouton. Le doute profite à l'utilisateur — quatre
 * pixels ne cachent aucune ligne, un cul-de-sac coûte l'application.
 */
const READ_END_TOLERANCE_PX = 4

/**
 * Le corps a-t-il été lu jusqu'au bout ?
 *
 * Rend vrai quand il n'y a rien à faire défiler : `scrollHeight` vaut alors
 * `clientHeight`, et la condition est satisfaite dès la première mesure.
 */
export function readToEnd(
  scrollTop: number,
  clientHeight: number,
  scrollHeight: number,
): boolean {
  return scrollTop + clientHeight >= scrollHeight - READ_END_TOLERANCE_PX
}

/**
 * Dialogues ouverts, du plus ancien au plus récent.
 *
 * Un dialogue peut s'ouvrir par-dessus un autre : l'ajout d'un marqueur, la
 * liste restant ouverte dessous pour enchaîner les ajouts. Comme chacun écoute
 * la touche d'échappement sur le document, une seule pression les fermerait
 * tous : la pile dit lequel doit répondre.
 */
const openDialogs: HTMLElement[] = []

/**
 * Le dialogue est-il celui du dessus ?
 *
 * Le clic n'a pas besoin de la question — le voile du dessus reçoit l'événement
 * de lui-même —, mais la touche d'échappement s'écoute sur le document, où rien
 * ne dit qui est visible.
 */
export function isTopDialog<T>(stack: readonly T[], dialog: T): boolean {
  return stack.length > 0 && stack[stack.length - 1] === dialog
}

/**
 * Change le titre du dialogue qui contient cet élément.
 *
 * Le titre est posé à l'ouverture, quand le corps ne sait pas encore ce qu'il
 * montrera : le sélecteur de type du dialogue d'ajout, lui, le fait varier —
 * « Ajouter un marqueur » ou « Départ réel ». Remonter depuis le corps évite
 * d'ouvrir le titre à la modification pour tous les dialogues, quand un seul
 * en a besoin.
 *
 * Sans effet tant que l'élément n'est pas dans un dialogue : le formulaire est
 * construit avant d'y être posé, et sa première synchronisation n'a alors rien
 * à titrer.
 */
export function setDialogTitle(inside: HTMLElement, title: string): void {
  const heading = inside.closest('.dialog')?.querySelector('.dialog__title')
  if (heading) heading.textContent = title
}

/** Affiche une boîte de dialogue et renvoie une fonction de fermeture. */
export function showDialog(
  container: HTMLElement,
  options: DialogOptions,
): () => void {
  const backdrop = document.createElement('div')
  backdrop.className = 'dialog-backdrop'

  const dialog = document.createElement('div')
  dialog.className = 'dialog'
  dialog.setAttribute('role', 'dialog')
  dialog.setAttribute('aria-modal', 'true')

  const title = document.createElement('h2')
  title.className = 'dialog__title'
  title.textContent = options.title
  dialog.append(title)

  let scrollBox: HTMLElement | null = null

  if (options.body !== undefined) {
    const body = document.createElement('div')
    body.className = 'dialog__body'
    if (typeof options.body === 'string') {
      // `textContent` plutôt qu'`innerHTML` : les noms de traces viennent de
      // fichiers tiers et ne doivent jamais être interprétés comme du balisage.
      body.textContent = options.body
      // Les sauts de ligne du message deviennent des sauts rendus. Réservé au
      // corps textuel : appliqué à tous, il ferait aussi respecter les
      // retours à la ligne du code source dans les corps construits en DOM.
      body.classList.add('dialog__body--text')
    } else {
      body.append(options.body)
    }
    dialog.append(body)
    scrollBox = body
  }

  let closed = false
  const close = (): void => {
    if (closed) return
    closed = true
    document.removeEventListener('keydown', onKeyDown)
    // Retiré par identité, jamais dépilé : un dialogue du dessous peut se
    // fermer le premier — la liste des marqueurs s'efface ainsi pour laisser
    // la carte au choix d'un point de rentrée.
    const rang = openDialogs.indexOf(backdrop)
    if (rang >= 0) openDialogs.splice(rang, 1)
    backdrop.remove()
    options.onClose?.()
  }

  const bloques: HTMLButtonElement[] = []
  let rappel: HTMLElement | null = null

  if (options.actions.length > 0) {
    if (options.readingHint !== undefined) {
      rappel = document.createElement('p')
      rappel.className = 'dialog__reading-hint'
      rappel.textContent = options.readingHint
      dialog.append(rappel)
    }

    const actions = document.createElement('div')
    actions.className = 'dialog__actions'

    for (const action of options.actions) {
      const button = document.createElement('button')
      button.type = 'button'
      // L'action de validation est mise en avant, comme dans les dialogues
      // Material d'Android. Le drapeau `primary` existait depuis l'origine sans
      // que rien ne le lise : tous les boutons se ressemblaient, et la réponse
      // attendue ne se distinguait pas de l'abandon.
      button.className = action.destructive
        ? 'dialog__action dialog__action--destructive'
        : action.primary
          ? 'dialog__action dialog__action--primary'
          : 'dialog__action'
      button.textContent = action.label
      if (action.readingRequired === true) {
        // Bloqué dès la construction : activé par la première mesure, jamais
        // l'inverse. Un bouton libre le temps d'une image se clique.
        button.disabled = true
        bloques.push(button)
      }
      button.addEventListener('click', () => {
        action.onClick?.()
        if (!action.keepOpen) close()
      })
      actions.append(button)
    }

    dialog.append(actions)
  }

  const onKeyDown = (event: KeyboardEvent): void => {
    if (
      event.key === 'Escape' &&
      options.dismissible !== false &&
      isTopDialog(openDialogs, backdrop)
    ) {
      close()
    }
  }

  if (options.dismissible !== false) {
    backdrop.addEventListener('click', (event) => {
      if (event.target === backdrop) close()
    })
  }
  document.addEventListener('keydown', onKeyDown)

  backdrop.append(dialog)
  container.append(backdrop)
  openDialogs.push(backdrop)

  // Le focus part sur le dialogue pour que la navigation au clavier et les
  // lecteurs d'écran ne restent pas derrière le voile.
  dialog.tabIndex = -1
  dialog.focus()

  /*
   * Le déblocage se mesure après insertion, et se réévalue à ce dont il
   * dépend.
   *
   * Avant l'insertion, toutes les hauteurs valent zéro : la condition serait
   * satisfaite d'emblée, et le bouton libre sur un texte jamais lu. Ensuite,
   * deux causes déplacent la borne sans qu'aucun défilement n'ait lieu — la
   * boîte qui change de taille (rotation, clavier virtuel) et le contenu qui
   * grandit (police de secours remplacée). Observer les deux couvre aussi
   * celles qu'on n'a pas imaginées ; aucune des deux n'est redimensionnée
   * ici, la boucle ne se referme donc pas.
   */
  if (bloques.length > 0 && scrollBox) {
    const boite = scrollBox
    const mesurer = (): void => {
      const lu = readToEnd(boite.scrollTop, boite.clientHeight, boite.scrollHeight)
      for (const button of bloques) button.disabled = !lu
      if (rappel) rappel.hidden = lu
    }

    boite.addEventListener('scroll', mesurer, { passive: true })
    const observer = new ResizeObserver(mesurer)
    observer.observe(boite)
    if (boite.firstElementChild) observer.observe(boite.firstElementChild)
    mesurer()
  } else if (rappel) {
    rappel.hidden = true
  }

  return close
}

/** Demande de confirmation avant une action irréversible. */
export function confirmDialog(
  container: HTMLElement,
  options: {
    title: string
    message: string
    confirmLabel: string
    cancelLabel: string
    onConfirm: () => void
  },
): void {
  showDialog(container, {
    title: options.title,
    body: options.message,
    actions: [
      { label: options.cancelLabel },
      { label: options.confirmLabel, onClick: options.onConfirm, primary: true },
    ],
  })
}

export interface ChoiceOption<T> {
  readonly value: T
  readonly label: string
  readonly selected?: boolean
}

/** Liste de choix exclusifs, équivalent d'un dialogue à boutons radio. */
export function chooseDialog<T>(
  container: HTMLElement,
  options: {
    title: string
    choices: readonly ChoiceOption<T>[]
    cancelLabel: string
    onChoose: (value: T) => void
    /** Appelé à toute fermeture, y compris par abandon. */
    onClose?: () => void
  },
): void {
  const list = document.createElement('div')

  let close = (): void => {}

  for (const choice of options.choices) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'dialog__option'

    const radio = document.createElement('input')
    radio.type = 'radio'
    radio.name = 'choice'
    radio.checked = choice.selected === true
    radio.tabIndex = -1

    const label = document.createElement('span')
    label.textContent = choice.label

    button.append(radio, label)
    button.addEventListener('click', () => {
      options.onChoose(choice.value)
      close()
    })

    list.append(button)
  }

  close = showDialog(container, {
    title: options.title,
    body: list,
    actions: [{ label: options.cancelLabel }],
    onClose: options.onClose,
  })
}

/** Saisie d'une valeur texte, avec sélection automatique du contenu. */
export function promptDialog(
  container: HTMLElement,
  options: {
    title: string
    initialValue: string
    okLabel: string
    cancelLabel: string
    onSubmit: (value: string) => void
  },
): void {
  const input = document.createElement('input')
  input.type = 'text'
  input.value = options.initialValue
  input.style.width = '100%'
  input.style.padding = '12px'
  input.style.fontSize = '16px'
  input.style.borderRadius = '4px'
  input.style.border = '1px solid var(--md-outline)'
  input.style.background = 'transparent'
  input.style.color = 'inherit'

  const close = showDialog(container, {
    title: options.title,
    body: input,
    actions: [
      { label: options.cancelLabel },
      {
        label: options.okLabel,
        onClick: () => options.onSubmit(input.value.trim()),
        primary: true,
      },
    ],
  })

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      options.onSubmit(input.value.trim())
      close()
    }
  })

  input.focus()
  input.select()
}
